/**
 * Discounts card (Manage Client) — a standing discount for ONE client:
 * a percent off credit packs and a percent off the $500 platform fee, with an
 * optional end date. Saved through the `adminSetClientDiscount` callable, which
 * validates 0-90, writes an audit row in the same transaction and emails James.
 * The fields cannot be changed from the browser directly (firestore.rules).
 *
 * Self-contained on purpose: it listens to the client doc itself, so
 * ManageClient only has to mount it.
 *
 * 0 is a real value here. Never read these fields with `|| default` — that
 * pattern turns a saved 0 into the default elsewhere on this page.
 */

import { useEffect, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';

const MAX_PCT = 90;
const CONFIRM_ABOVE_PCT = 25;

interface SaveResult {
  success: boolean;
  changed: boolean;
  creditDiscountPct: number;
  platformFeeDiscountPct: number;
  expiresAtMs: number | null;
  expiredSessions: number;
}

const asPct = (v: unknown): number => (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_PCT ? v : 0);

/** Whole number 0-90 from a text box, or null when it is not one. */
function parsePctText(t: string): number | null {
  if (!/^\d{1,2}$/.test(t.trim())) return null;
  const n = Number(t.trim());
  return n >= 0 && n <= MAX_PCT ? n : null;
}

/** YYYY-MM-DD in Central time for a stored end-of-day timestamp. */
function centralDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
}

const usd = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ClientDiscountCard({ clientId }: { clientId: string }) {
  const [savedCredit, setSavedCredit] = useState(0);
  const [savedPlatform, setSavedPlatform] = useState(0);
  const [savedEndsOn, setSavedEndsOn] = useState('');
  const [savedEndsAtMs, setSavedEndsAtMs] = useState<number | null>(null);
  const [savedExpiryUnreadable, setSavedExpiryUnreadable] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  // Shown on the Save button and in the confirm box so it is always clear WHO gets the discount.
  const [clientLabel, setClientLabel] = useState(clientId);

  const [creditText, setCreditText] = useState('0');
  const [platformText, setPlatformText] = useState('0');
  const [endsOn, setEndsOn] = useState('');
  const [note, setNote] = useState('');
  const [dirty, setDirtyState] = useState(false);
  // Mirror of `dirty` for the snapshot listener (which must not re-subscribe on every keystroke).
  const dirtyRef = useRef(false);
  const setDirty = (v: boolean) => { dirtyRef.current = v; setDirtyState(v); };

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    // A different client: drop EVERYTHING typed for the previous one. Without
    // this, half-typed percents (kept by dirtyRef) could be saved onto the new
    // client. ManageClient also mounts this card with key={clientId}.
    dirtyRef.current = false;
    setDirtyState(false);
    setCreditText('0');
    setPlatformText('0');
    setEndsOn('');
    setNote('');
    setMessage('');
    setIsError(false);
    const unsub = onSnapshot(doc(db, 'clients', clientId), snap => {
      const d = snap.data() ?? {};
      const credit = asPct(d.creditDiscountPct);
      const platform = asPct(d.platformFeeDiscountPct);
      const rawExpiry = d.discountExpiresAt;
      const endsMs: number | null = rawExpiry?.toMillis ? rawExpiry.toMillis() : null;
      // Same rule as the server: an end date that is set but unreadable counts as ended.
      const unreadable = rawExpiry != null && endsMs === null;
      const endedNow = unreadable || (endsMs !== null && endsMs <= Date.now());
      const ends = endsMs !== null ? centralDate(endsMs) : '';
      setSavedEndsAtMs(endsMs);
      setSavedExpiryUnreadable(unreadable);
      setClientLabel(
        (typeof d.name === 'string' && d.name) || (typeof d.email === 'string' && d.email) || clientId,
      );
      setSavedCredit(credit);
      setSavedPlatform(platform);
      setSavedEndsOn(ends);
      setUpdatedAt(d.discountUpdatedAt?.toDate ? d.discountUpdatedAt.toDate() : null);
      // Do not overwrite what the admin is in the middle of typing. An ENDED
      // discount pre-fills as what is really in force: 0 / 0 / no date.
      if (!dirtyRef.current) {
        setCreditText(String(endedNow ? 0 : credit));
        setPlatformText(String(endedNow ? 0 : platform));
        setEndsOn(endedNow ? '' : ends);
      }
    });
    return () => unsub();
  }, [clientId]);

  const credit = parsePctText(creditText);
  const platform = parsePctText(platformText);
  const valid = credit !== null && platform !== null;
  // Worked out at RENDER time so a page left open past the end date is still right.
  const ended = savedExpiryUnreadable || (savedEndsAtMs !== null && savedEndsAtMs <= Date.now());
  // What is really in force (checkout treats an ended discount as 0%).
  const effCredit = ended ? 0 : savedCredit;
  const effPlatform = ended ? 0 : savedPlatform;
  const effEndsOn = ended ? '' : savedEndsOn;
  const unchanged = valid && credit === effCredit && platform === effPlatform && endsOn === effEndsOn;

  const edit = (fn: () => void) => { fn(); setDirty(true); setMessage(''); };

  const save = async () => {
    if (!valid || credit === null || platform === null) return;
    if (Math.max(credit, platform) > CONFIRM_ABOVE_PCT) {
      const ok = window.confirm(
        `Save a ${Math.max(credit, platform)}% discount for ${clientLabel}?\n\n` +
        `Credit packs: ${credit}% off  (the $375 pack becomes ${usd(375 * (1 - credit / 100))})\n` +
        `Platform fee: ${platform}% off  ($500 becomes ${usd(500 * (1 - platform / 100))})\n\n` +
        'It applies to EVERY purchase this client makes until you change it' +
        `${endsOn ? ` or it ends on ${endsOn}` : ''}.`,
      );
      if (!ok) return;
    }
    setSaving(true);
    setMessage('');
    setIsError(false);
    try {
      const fn = httpsCallable<Record<string, unknown>, SaveResult>(functions, 'adminSetClientDiscount');
      const { data } = await fn({
        clientId, creditDiscountPct: credit, platformFeeDiscountPct: platform,
        expiresOn: endsOn || null, note,
      });
      setDirty(false);
      setNote('');
      setMessage(
        data.changed
          ? `Saved. Credit packs ${data.creditDiscountPct}% off, platform fee ${data.platformFeeDiscountPct}% off` +
            `${data.expiredSessions > 0 ? `. ${data.expiredSessions} open checkout(s) at the old price were expired` : ''}.`
          : 'No change — those values were already saved.',
      );
    } catch (err) {
      setIsError(true);
      setMessage(`Error: ${err instanceof Error ? err.message : 'could not save'}`);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '70px', padding: '8px', textAlign: 'right' as const };

  return (
    <section style={{ marginBottom: '32px', padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
      <h3 style={{ marginTop: 0 }}>Discounts (standing, this client only)</h3>
      {ended && (savedCredit > 0 || savedPlatform > 0) ? (
        <p style={{ color: 'var(--gold)', marginTop: '4px', marginBottom: '8px', fontSize: '0.9rem' }}>
          Now: <strong>no discount</strong> &mdash; the {savedCredit}% credits / {savedPlatform}% platform fee discount
          ended{savedEndsOn ? ` on ${savedEndsOn}` : ''}. This client pays list price. Save new values to start another.
        </p>
      ) : (
        <p style={{ opacity: 0.8, marginTop: '4px', marginBottom: '8px', fontSize: '0.9rem' }}>
          Now: credit packs <strong>{effCredit}% off</strong> &middot; platform fee <strong>{effPlatform}% off</strong>
          {effEndsOn ? <span> &middot; ends {effEndsOn}</span> : (effCredit > 0 || effPlatform > 0) ? <span> &middot; no end date</span> : null}
          {updatedAt && <span> &middot; last changed {updatedAt.toLocaleDateString()}</span>}
        </p>
      )}
      <p style={{ opacity: 0.7, marginTop: 0, marginBottom: '12px', fontSize: '0.85rem' }}>
        Taken off the LIST price of every purchase this client makes. The client still receives the full credits.
        It does not apply to Lead Data Cleaning or Buy New Leads. Whole numbers, 0 to {MAX_PCT}. Every change is
        written to the admin audit log and emailed to you.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end', marginBottom: '12px' }}>
        <label style={{ fontSize: '0.85rem' }}>
          Credit packs % off<br />
          <input type="text" inputMode="numeric" value={creditText} disabled={saving}
            onChange={e => edit(() => setCreditText(e.target.value))} style={inputStyle} />
        </label>
        <label style={{ fontSize: '0.85rem' }}>
          Platform fee % off<br />
          <input type="text" inputMode="numeric" value={platformText} disabled={saving}
            onChange={e => edit(() => setPlatformText(e.target.value))} style={inputStyle} />
        </label>
        <label style={{ fontSize: '0.85rem' }}>
          Ends on (optional, Central time)<br />
          <input type="date" value={endsOn} disabled={saving}
            onChange={e => edit(() => setEndsOn(e.target.value))} style={{ padding: '7px' }} />
        </label>
        <label style={{ fontSize: '0.85rem', flex: '1 1 220px' }}>
          Note for the audit log (the client never sees it)<br />
          <input type="text" value={note} maxLength={300} disabled={saving}
            onChange={e => setNote(e.target.value)} placeholder="e.g. long-term client" style={{ width: '100%', padding: '8px' }} />
        </label>
      </div>

      {valid && credit !== null && platform !== null ? (
        <p style={{ opacity: 0.8, marginTop: 0, marginBottom: '12px', fontSize: '0.85rem' }}>
          Preview: the $375 credit pack &rarr; <strong>{usd(375 * (1 - credit / 100))}</strong> &middot;
          the $500 platform fee &rarr; <strong>{usd(500 * (1 - platform / 100))}</strong>
        </p>
      ) : (
        <p style={{ color: 'var(--red)', marginTop: 0, marginBottom: '12px', fontSize: '0.85rem' }}>
          Each percent must be a whole number from 0 to {MAX_PCT}.
        </p>
      )}

      <button type="button" className="btn btn-primary" onClick={save} disabled={saving || !valid || unchanged}>
        {saving ? 'Saving…' : `Save discounts for ${clientLabel}`}
      </button>
      {dirty && !saving && (
        <button type="button" className="btn btn-secondary" style={{ marginLeft: '8px' }}
          onClick={() => { setCreditText(String(effCredit)); setPlatformText(String(effPlatform)); setEndsOn(effEndsOn); setDirty(false); setMessage(''); }}>
          Undo edits
        </button>
      )}
      {message && (
        <span style={{ marginLeft: '12px', color: isError ? 'var(--red)' : 'var(--green)' }}>{message}</span>
      )}
    </section>
  );
}
